import React, { forwardRef } from "react";
import { Section, Text } from "@/components";
import "./SmartFeatures.scss";

const data = [
  {
    title: "AI-Generated Checklists",
    description: "Upload a deck or data room and IntelliRoom™ will generate a tailored checklist of the most relevant diligence questions for each deal.",
    icon: "/images/home/checklist.svg"
  },
  {
    title: "Source-Backed Answers",
    description: "For every question, IntelliRoom™ provides a detailed, source-cited answer along with a concise executive summary",
    icon: "/images/home/source.svg"
  },
  {
    title: "Risk & Opportunity Flags",
    description: "Each checklist item is tagged with red, yellow, or green flags to spotlight issues and opportunities that warrant further investigation.",
    icon: "/images/home/flag.svg"
  },
  {
    title: "Excel-Native Support",
    description: "IntelliRoom™ can parse .xls workbooks, extract totals and key figures, and even perform calculations on xls based data sets, something generic LLMs simply can’t do reliably.",
    icon: "/images/home/excel.svg"
  },
  {
    title: "Private LLM Workspace",
    description: "A secure & private LLM built for deeper research, memo drafting, and follow-up analysis, bringing the power of a familiar chat experience to your workspace",
    icon: "/images/home/private_locked.svg"
  },
  {
    title: "Collaborative & Transparent",
    description: "One shared workspace for your entire team. All members can view the same questions, answers, and flags, creating a single source of truth for your diligence process.",
    icon: "/images/home/collaboration.svg"
  },
];

const mobileData = [
  {
    title: "AI-Generated Checklists",
    description: "Upload a website, deck, or full data room and IntelliRoom™ creates a deal-specific checklist of key diligence questions.",
    icon: "/images/home/checklist.svg"
  },
  {
    title: "Source-Backed Answers",
    description: "For every question, IntelliRoom™ provides a detailed, source-cited answer along with a concise executive summary",
    icon: "/images/home/source.svg"
  },
  {
    title: "Risk & Opportunity Flags",
    description: "Checklist items are flagged (red/yellow/green) to highlight risks and opportunities that warrant further investigation.",
    icon: "/images/home/flag.svg"
  },
  {
    title: "Excel-Native Support",
    description: "IntelliRoom™ reads .xls files, extracts key figures, and runs calculations - unlike generic LLMs.",
    icon: "/images/home/excel.svg"
  },
  {
    title: "Private LLM Workspace",
    description: "A secure & private LLM built for deeper research, memos, and analysis all in a familiar chat format.",
    icon: "/images/home/private_locked.svg"
  },
  {
    title: "Collaborative & Transparent",
    description: "One workspace for your team shared questions, answers, and flags in one source of truth.",
    icon: "/images/home/collaboration.svg"
  },
];

export const SmartFeatures = forwardRef<HTMLElement, {}>((props, ref) => {
  return (
    <Section
      ref={ref}
      id="features-section"
      className="w-full mx-0 !px-0 md:py-0 md:pb-[64px] lg:pb-0"
    >
      <div className="w-full pt-[46px] md:px-[40px] lg:p-[120px] mt-[-100px] md:mt-[-60px] rounded-t-[20px] md:rounded-t-[60px] bg-black">
        <div className="md:max-w-[1200px] mx-auto">
          <div>
            <div className="text-left flex flex-col items-center md:items-start">
              <div className="flex flex-row gap-y-4 md:gap-x-1">
                <Text.Heading size="xl" className="text-white text-[36px] leading-[38px] md:text-[74px] md:leading-[84px]">
                  Smart
                </Text.Heading>&nbsp;
                <Text.Heading size="xl" className="smart-features-section-title text-[36px] leading-[38px] md:text-[74px] md:leading-[84px]">
                  Features
                </Text.Heading>
              </div>
              
              <Text.Heading size="xl" className="smart-features-section-title2 text-[36px] leading-[38px] md:text-[74px] md:leading-[84px]">
                Efficient Workflow
              </Text.Heading>
            </div>

            <Text.Description size="md" className="!text-[#C7C7C7] font-400 mt-1 md:mt-4 text-center md:text-left">
              Everything you need to run fast, accurate<br />
              diligence, all in one <strong className="text-white">AI-powered workspace</strong>.
            </Text.Description>
          </div>

          {/* desktop version */}
          <div className="hidden md:block mt-8 md:mt-[72px] px-4 md:px-0">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-[60px]">
              {data.map((item, index) => (
                <div
                  key={`smart-feature-${index}`}
                  className="smart-feature-card"
                >
                  <div className="smart-feature-card-content flex flex-row md:flex-col items-center md:items-start gap-4 md:gap-3 md:border-none md:p-0">
                    <div className="mb-0 md:mb-3 w-[74px] h-[74px]">
                      <img
                        src={item.icon}
                        alt={item.title}
                        className="w-full h-full"
                        loading="lazy"
                      />
                    </div>

                    <div className="flex-1 flex flex-col md:gap-3">
                      <div className="text-white text-[18px] leading-[36px] md:text-[26px] md:leading-[36px] font-inter">
                        {item.title}
                      </div>
                      <Text.Description size="md" className="!text-[#8A8C93] md:!text-[14px] md:!leading-[20px]">
                        {item.description}
                      </Text.Description>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* mobile version */}
          <div className="block md:hidden mt-8 md:mt-[72px] px-4 md:px-0">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-[60px]">
              {mobileData.map((item, index) => (
                <div
                  key={`smart-feature-${index}`}
                  className="smart-feature-card"
                >
                  <div className="smart-feature-card-content flex flex-row md:flex-col items-center md:items-start gap-4 md:gap-3 md:border-none md:p-0">
                    <div className="mb-0 md:mb-3 w-[74px] h-[74px]">
                      <img
                        src={item.icon}
                        alt={item.title}
                        className="w-full h-full"
                        loading="lazy"
                      />
                    </div>

                    <div className="flex-1 flex flex-col md:gap-3">
                      <div className="smart-feature-card-title text-white text-[18px] leading-[36px] md:text-[26px] md:leading-[36px] font-inter">
                        {item.title}
                      </div>
                      <Text.Description size="md" className="smart-feature-card-description !text-[#8A8C93] md:!text-[14px] md:!leading-[20px]">
                        {item.description}
                      </Text.Description>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
});
